/**
 * The comparison workbench's engine — ALL of it frontend, by design.
 *
 * Contract §3 and infra C.5 are explicit: the API returns raw offers and an FX snapshot, and
 * "no allocation math server-side". Every function here recomputes on a click, so a
 * round-trip per interaction would make the screen unusable. Only the chosen allocation is
 * persisted (`comparison.save_comparison`).
 *
 * Two normalisations happen before any offer is ranked, and both matter:
 *
 *   CURRENCY — rates arrive in the supplier's own currency (`ComparisonSupplier.currency`);
 *              comparison happens in one common `baseCurrency`, via `fxLookup`. An offer whose
 *              currency has no rate to `baseCurrency` is EXCLUDED from ranking — never compared
 *              on its raw number, which would silently prefer a cheap-looking USD offer over a
 *              genuinely cheaper AED one.
 *   UOM      — a rate is per ONE quoted unit, which is not always the unit the RFQ asked
 *              for. A price per box ranked against prices per piece is wrong by the pack
 *              factor, so offers are restated per RFQ UOM using `item.uom_conversions`
 *              (contract §8). An offer that cannot be converted is likewise EXCLUDED rather
 *              than ranked on a number known to be wrong.
 *
 * ---- Task 10: the allocation split is a flat list, not a per-item map ----
 *
 * The old `Allocations` (`Record<item_code, Record<supplier, qty>>`) could hold at most one
 * quantity per supplier per item — never "200 from A, 100 from B" against the SAME item unless
 * the two suppliers happened to be different keys in the same map, which is exactly what it
 * did hold, so the shape LOOKED sufficient. What it structurally could not hold is two
 * suppliers against the same (item, enquiry line) pulled from a wire that sends one row PER
 * ENQUIRY LINE, not per item code (`ComparisonItem.enquiry_row_no`) — a customer asking for the
 * same item on two lines needs two independent allocations, and `item_code` alone cannot tell
 * them apart. `AllocationRow[]` (`src/lib/types.ts`) fixes both: several rows may share an
 * `item_code`, keyed apart by `enquiry_row_no` + `supplier`.
 *
 * Every function below that used to take/return `Record<supplier, qty>` for one item now
 * takes/returns `AllocationRow[]` scoped to that item's rows (its `rowKey`); `computeAllocation`
 * and `pruneAllocations`, which operate across the whole matrix, take/return the flat list
 * directly. Supplier identity throughout is `Supplier.code` — the SAME string
 * `ComparisonItem.offers` is keyed by — never a display name (task 8b); a caller that wants a
 * label resolves one through `ComparisonSupplier.supplier_name`.
 */

import { fxLookup, type WireFx } from './api/wire'
import { uomFactor } from './format'
import type { AllocationRow, ComparisonItem, ComparisonLine, Currency, Item, Offer } from './types'

export const STRATEGIES = ['cheapest', 'availability', 'fewest', 'single'] as const
export type Strategy = (typeof STRATEGIES)[number]

/**
 * Quantities and money are compared with a tolerance, never with `===`.
 *
 * Converting between UOMs and currencies divides, so a line that is exactly covered can land
 * on 299.99999999999994 instead of 300. An exact test then renders "2.8e-14 left" on a
 * covered line and miscounts the "lines covered" stat. The epsilon lives here so every
 * caller shares one definition rather than each component inventing its own.
 */
const EPSILON = 1e-6

const roughlyEqual = (a: number, b: number): boolean => Math.abs(a - b) < EPSILON
/** Rounds away float residue while leaving genuine fractions intact. */
const tidy = (n: number): number => Math.round(n * 1e6) / 1e6

export const STRATEGY_LABELS: Record<Strategy, { title: string; hint: string }> = {
  cheapest: { title: 'Cheapest per item', hint: 'lowest unit rate' },
  availability: { title: 'Best availability', hint: 'auto-split to cover stock' },
  fewest: { title: 'Fewest suppliers', hint: 'consolidate' },
  single: { title: 'Single best supplier', hint: 'one PO' },
}

/** An offer restated in base currency per RFQ UOM — the only form safe to rank. */
export interface NormalisedOffer {
  supplier: string
  /** Base-currency price for one unit in the RFQ's UOM. */
  unitInBase: number
  /** Stock restated in the RFQ's UOM. */
  stock: number
  /** The offer as it arrived, for display. */
  raw: Offer
  /** Quoted in a different UOM than the RFQ asked for. */
  converted: boolean
  /** Units of RFQ UOM per quoted unit; 1 when there was nothing to convert. */
  factor: number
}

/**
 * How many RFQ-UOM units make up one quoted unit. `null` when the master can't say — the
 * signal to exclude the offer.
 */
function conversionFactor(offer: Offer, master: Item | undefined): number | null {
  if (!offer.uom_mismatch || offer.sq_uom === offer.rfq_uom) return 1
  if (!master) return null
  const sq = uomFactor(master, offer.sq_uom)
  const rfq = uomFactor(master, offer.rfq_uom)
  if (sq === null || rfq === null || rfq === 0) return null
  return sq / rfq
}

/** Offers that could not be normalised, so the screen can say why a supplier is missing. */
export interface ExcludedOffer {
  item_code: string
  supplier: string
  reason: string
  /** Kept so the screen can still show what they quoted, clearly marked as not ranked. */
  raw: Offer
}

export interface NormalisedItem {
  item_code: string
  name: string
  qty: number
  /** The unit `qty` is counted in — every offer on the row is ranked per one of these. */
  uom: string
  /** The STRING through-key back to the enquiry line — see `ComparisonItem.enquiry_row_no`.
   * Part of an `AllocationRow`'s identity alongside `item_code` + `supplier`. */
  enquiry_row_no: string
  /** The customer line(s) this row covers — today always one (see `enquiry_row_no` above). */
  lines: ComparisonLine[]
  /** Rankable offers, cheapest first. */
  offers: NormalisedOffer[]
  bySupplier: Map<string, NormalisedOffer>
  excluded: ExcludedOffer[]
}

/**
 * One entry per supplier, holding their CHEAPEST offer.
 *
 * The supplier COLUMNS are per supplier by construction, so this map has to collapse a supplier
 * who quoted the same line more than once — which the live server really does send (five
 * `Supplier Quotation`s under one supplier code on ENQ-2026-00018). Collapsing on price is at
 * least a rule someone can reason about, unlike the arbitrary last-one-wins a keyed wire gave.
 *
 * Nothing is lost for RANKING: `NormalisedItem.offers` keeps every quotation, so the strategies
 * and the best-per-item still see them all. Only the column display is narrowed.
 */
function cheapestPerSupplier(sorted: readonly NormalisedOffer[]): Map<string, NormalisedOffer> {
  const map = new Map<string, NormalisedOffer>()
  // `sorted` is already cheapest-first, so the first entry for a code is that supplier's best.
  for (const offer of sorted) if (!map.has(offer.supplier)) map.set(offer.supplier, offer)
  return map
}

/**
 * Normalise the whole matrix once per data change. Zero rates are dropped — the prototype treats
 * rate 0 as "no offer".
 */
export function normaliseComparison(
  items: ComparisonItem[],
  itemsByCode: Map<string, Item>,
  fx: WireFx,
  baseCurrency: Currency,
): NormalisedItem[] {
  return items.map((item) => {
    const master = itemsByCode.get(item.item_code)
    const offers: NormalisedOffer[] = []
    const excluded: ExcludedOffer[] = []

    for (const offer of item.offers) {
      const supplier = offer.supplier
      if (offer.rate.amount <= 0) continue

      const factor = conversionFactor(offer, master)
      if (factor === null) {
        excluded.push({
          item_code: item.item_code,
          supplier,
          reason: `quoted in ${offer.sq_uom}, no conversion to ${offer.rfq_uom} in the item master`,
          raw: offer,
        })
        continue
      }

      /*
        CURRENCY. The server's own converted figure wins when it sends one: that is the number it
        will price the quotation with, and ranking on a different one means the workbench's
        cheapest is not the quotation's cheapest.

        Only when it sends none do we convert through the FX snapshot. An offer that has neither is
        EXCLUDED, never ranked on its raw number — the newer payload carries no `fx` at all, so
        without this an unconverted USD offer would be compared against AED as though 1 USD = 1 AED
        and win every allocation.
      */
      const inBase = offer.rate_in_base?.amount ?? null
      const fxRate = inBase === null ? fxLookup(fx, offer.rate.currency, baseCurrency) : null
      if (inBase === null && fxRate === null) {
        excluded.push({
          item_code: item.item_code,
          supplier,
          reason: `quoted in ${offer.rate.currency}, no FX rate to ${baseCurrency} and no converted rate from the server`,
          raw: offer,
        })
        continue
      }

      offers.push({
        supplier,
        unitInBase: (inBase ?? offer.rate.amount * (fxRate ?? 1)) / factor,
        // Floored: stock restated into RFQ units can come out fractional when the supplier
        // quoted in a smaller unit, and you cannot order a fraction of an RFQ unit. Without
        // this the availability split writes "4.1666 boxes" into the saved allocation.
        stock: Math.floor(offer.stock * factor),
        raw: offer,
        converted: factor !== 1,
        factor,
      })
    }

    offers.sort((a, b) => a.unitInBase - b.unitInBase)
    return {
      item_code: item.item_code,
      name: item.name,
      qty: item.qty,
      uom: item.uom,
      enquiry_row_no: item.enquiry_row_no,
      lines: item.lines,
      offers,
      /*
        One entry per supplier, holding their CHEAPEST offer — `offers` is sorted by then, so the
        first write per code wins and later, dearer ones are skipped.

        This map exists for the supplier COLUMNS, which are per supplier by construction. It is
        lossy where a supplier has quoted twice, and deliberately so: `offers` above keeps every
        quotation, so the strategies and the ranking still see them all. Making the columns
        themselves per-quotation is the next stage of this migration.
      */
      bySupplier: cheapestPerSupplier(offers),
      excluded,
    }
  })
}

/**
 * The lowest normalised unit price on an item, or `null` when nobody quoted it.
 *
 * Pass `visibleSuppliers` to rank within the suppliers currently shown: hiding the cheapest
 * supplier promotes the next one to "best", exactly as the prototype does (line 863), rather
 * than leaving the row with no highlight at all.
 */
export function bestUnitPrice(item: NormalisedItem, visibleSuppliers?: readonly string[]): number | null {
  if (!visibleSuppliers) return item.offers[0]?.unitInBase ?? null
  const visible = item.offers.filter((o) => visibleSuppliers.includes(o.supplier))
  return visible[0]?.unitInBase ?? null
}

/** Whether this offer is the cheapest on its row, compared with tolerance. */
export const isBestOffer = (offer: NormalisedOffer, best: number | null): boolean =>
  best !== null && roughlyEqual(offer.unitInBase, best)

/* ============ allocation rows ============ */

/** The rows of a flat allocation list that belong to one item's row (`item_code` +
 * `enquiry_row_no`) — every per-item function below scopes the full list down to this first. */
export function rowsForItem(allocations: AllocationRow[], item: NormalisedItem): AllocationRow[] {
  return allocations.filter((row) => row.item_code === item.item_code && row.enquiry_row_no === item.enquiry_row_no)
}

/** Replace one item's rows within the full flat list, leaving every other item's rows alone. */
export function withItemRows(allocations: AllocationRow[], item: NormalisedItem, rows: AllocationRow[]): AllocationRow[] {
  const others = allocations.filter(
    (row) => !(row.item_code === item.item_code && row.enquiry_row_no === item.enquiry_row_no),
  )
  return [...others, ...rows]
}

/**
 * Build one allocation row from an offer. `allocated_qty` is in RFQ UOM (it's a slice of
 * `item.qty`), so `rate` MUST be too, or `rate × allocated_qty` is wrong by the pack factor —
 * a supplier quoting AED 24/box of 24 pcs, allocated 40 pcs, must save `rate: 1` (AED/pcs), not
 * `rate: 24` (AED/box), which would overstate the line 24×. `offer.factor` is "RFQ units per
 * quoted unit" (see `conversionFactor`), so dividing the quoted rate by it restates the rate
 * per RFQ unit; `factor` is `1` when nothing needed converting, so this is a no-op there.
 * `currency` is left as the supplier's own quoted currency — only the UNIT changes here, never
 * a currency conversion (that stays display-only, via `fxLookup` at the UI boundary).
 */
function makeRow(item: NormalisedItem, offer: NormalisedOffer, qty: number): AllocationRow {
  return {
    item_code: item.item_code,
    enquiry_row_no: item.enquiry_row_no,
    supplier: offer.supplier,
    allocated_qty: tidy(qty),
    rate: offer.raw.rate.amount / offer.factor,
    currency: offer.raw.rate.currency,
    // What the supplier actually wrote, before it was restated into the RFQ's unit — the figure
    // that goes on the purchase order. See `AllocationRow.quoted_rate`.
    quoted_rate: offer.raw.rate.amount,
    // The offer's own identity and words, carried to the quotation so the salesperson raising a PO
    // knows which quotation was accepted and what the supplier said about it.
    supplier_quotation: offer.raw.supplier_quotation,
    remark: offer.raw.remark,
  }
}

/* ============ strategies ============ */

/** Greedy cover: take from the cheapest supplier first, up to their stock. */
function coverByAvailability(item: NormalisedItem): AllocationRow[] {
  const rows: AllocationRow[] = []
  let remaining = item.qty

  for (const offer of item.offers) {
    if (remaining <= 0) break
    // Whole units only — `stock` is already floored, and `remaining` stays integral as long
    // as the required qty is, so this never produces a fractional order line.
    const take = Math.min(offer.stock, remaining)
    if (take > 0) {
      rows.push(makeRow(item, offer, take))
      remaining = tidy(remaining - take)
    }
  }
  // Nobody has enough stock; the shortfall still has to be bought somewhere, so it goes to
  // the cheapest supplier rather than leaving the line short.
  if (remaining > 0 && item.offers[0]) {
    const first = item.offers[0]
    const existing = rows.find((row) => row.supplier === first.supplier)
    if (existing) existing.allocated_qty = tidy(existing.allocated_qty + remaining)
    else rows.push(makeRow(item, first, remaining))
  }
  return rows
}

/**
 * The supplier who covers the most lines, cheapest as the tie-break — the basis of both
 * consolidation strategies.
 */
function bestSingleSupplier(items: NormalisedItem[]): string | null {
  const scores = new Map<string, { cover: number; cost: number }>()

  for (const item of items) {
    for (const offer of item.offers) {
      const score = scores.get(offer.supplier) ?? { cover: 0, cost: 0 }
      score.cover += 1
      score.cost += offer.unitInBase * item.qty
      scores.set(offer.supplier, score)
    }
  }

  const ranked = [...scores.entries()].sort(
    ([, a], [, b]) => b.cover - a.cover || a.cost - b.cost,
  )
  return ranked[0]?.[0] ?? null
}

/**
 * The starting allocation for a strategy, as a flat list across every item. Nothing here is
 * persisted until the user saves — it is a suggestion they then adjust by clicking cells.
 */
export function computeAllocation(items: NormalisedItem[], strategy: Strategy): AllocationRow[] {
  if (strategy === 'fewest' || strategy === 'single') {
    const preferred = bestSingleSupplier(items)
    const rows: AllocationRow[] = []
    for (const item of items) {
      const fromPreferred = preferred ? item.bySupplier.get(preferred) : undefined
      // The consolidated supplier doesn't carry this line; fall back to the cheapest who does.
      const offer = fromPreferred ?? item.offers[0]
      if (offer) rows.push(makeRow(item, offer, item.qty))
    }
    return rows
  }

  const rows: AllocationRow[] = []
  for (const item of items) {
    if (item.offers.length === 0) continue
    if (strategy === 'availability') rows.push(...coverByAvailability(item))
    else rows.push(makeRow(item, item.offers[0], item.qty))
  }
  return rows
}

/** Auto-cover one line: the availability split, applied to a single item. Returns the rows
 * for just that item — merge with `withItemRows` to fold back into the full list. */
export const autoCoverItem = (item: NormalisedItem): AllocationRow[] => coverByAvailability(item)

/* ============ per-item summary ============ */

export interface ItemSummary {
  /** Total allocated quantity. */
  allocated: number
  /** Base-currency cost of the allocation. */
  cost: number
  /** Blended average unit price — the number the quotation takes as its cost. */
  avg: number
  /** Positive = still to allocate; negative = over-allocated. */
  remaining: number
  supplierCount: number
  covered: boolean
}

/** `allocation` must already be scoped to this item — see `rowsForItem`. */
export function summariseItem(item: NormalisedItem, allocation: AllocationRow[]): ItemSummary {
  let allocated = 0
  let cost = 0
  let supplierCount = 0

  for (const row of allocation) {
    const offer = item.bySupplier.get(row.supplier)
    // A supplier in the allocation with no current offer is skipped everywhere — quantity,
    // cost AND count — so the three never disagree. `pruneAllocations` removes them at the
    // boundary; this is the belt to that braces.
    if (!offer) continue
    allocated += row.allocated_qty
    cost += offer.unitInBase * row.allocated_qty
    supplierCount += 1
  }

  const tidyAllocated = tidy(allocated)
  return {
    allocated: tidyAllocated,
    cost: tidy(cost),
    avg: tidyAllocated > 0 ? cost / tidyAllocated : 0,
    remaining: tidy(item.qty - tidyAllocated),
    supplierCount,
    covered: tidyAllocated + EPSILON >= item.qty,
  }
}

/**
 * Drops rows a saved allocation names for an item/line/supplier combination that no longer
 * exists in this comparison — a stale split from before a supplier withdrew, an enquiry line
 * that was removed, or an enquiry whose RFQs were regenerated. Applied once when the
 * allocation is loaded, so every later calculation sees clean data.
 */
export function pruneAllocations(
  items: NormalisedItem[],
  allocations: AllocationRow[],
): { allocations: AllocationRow[]; dropped: Array<{ item_code: string; supplier: string }> } {
  const byRow = new Map(items.map((item) => [`${item.item_code}::${item.enquiry_row_no}`, item]))
  const cleaned: AllocationRow[] = []
  const dropped: Array<{ item_code: string; supplier: string }> = []

  for (const row of allocations) {
    const item = byRow.get(`${row.item_code}::${row.enquiry_row_no}`)
    if (!item || !item.bySupplier.has(row.supplier)) {
      dropped.push({ item_code: row.item_code, supplier: row.supplier })
      continue
    }
    cleaned.push(row)
  }

  return { allocations: cleaned, dropped }
}

/* ============ totals + previews ============ */

export interface ComparisonTotals {
  cost: number
  quantity: number
  blendedAvg: number
  suppliersUsed: number
  linesCovered: number
  lineCount: number
}

export function summariseAll(items: NormalisedItem[], allocations: AllocationRow[]): ComparisonTotals {
  let cost = 0
  let quantity = 0
  let linesCovered = 0
  const suppliers = new Set<string>()

  for (const item of items) {
    const rows = rowsForItem(allocations, item)
    const summary = summariseItem(item, rows)
    cost += summary.cost
    quantity += summary.allocated
    if (summary.covered) linesCovered += 1
    // Only suppliers with a live offer count as "used" — matching summariseItem.
    for (const row of rows) if (item.bySupplier.has(row.supplier)) suppliers.add(row.supplier)
  }

  return {
    // Rounded once at the boundary, not per line — sub-cent residue from hundreds of
    // divisions should not surface in the headline total.
    cost: tidy(cost),
    quantity: tidy(quantity),
    blendedAvg: quantity > 0 ? cost / quantity : 0,
    suppliersUsed: suppliers.size,
    linesCovered,
    lineCount: items.length,
  }
}

/* ============ UOM notes ============ */

/**
 * Every offer that needed a UOM restatement OR was excluded outright (a bad UOM conversion or
 * an unconvertible currency — see `normaliseComparison`'s module header), and what the
 * workbench did about it.
 *
 * The ranking is only trustworthy if the purchaser can see this: a rate quoted per box and a
 * rate quoted per piece look comparable side by side and are not, and neither does a rate
 * quoted in a currency with no FX rate to compare against. `converted` offers were restated and
 * are ranked honestly; the rest were dropped from the ranking entirely, and a supplier missing
 * from a row for that reason must be visible rather than silently absent — and visible for the
 * RIGHT reason: `reason` carries the actual cause (`normaliseComparison`'s own exclusion
 * message) rather than the panel guessing "no conversion in the item master" for every
 * exclusion, which is simply false for a currency-only exclusion (its UOM matched fine).
 */
export interface UomNote {
  item_code: string
  item_name: string
  /** Part of this note's identity alongside `item_code` + `supplier` — two enquiry lines can
   * share an item_code (see `ComparisonItem.enquiry_row_no`), and would otherwise collide. */
  enquiry_row_no: string
  supplier: string
  rfq_uom: string
  sq_uom: string
  /** `null` when the offer was excluded (either cause) rather than converted. */
  factor: number | null
  quoted: Offer['rate']
  /** The comparable rate, base currency per RFQ UOM. `null` for an excluded offer. */
  unitInBase: number | null
  handled: 'converted' | 'excluded'
  /** Why an excluded offer was dropped, verbatim from `normaliseComparison` — a UOM with no
   * conversion in the item master, or a currency with no FX rate to the base. Empty for a
   * `converted` offer, which needs no explanation beyond the factor shown alongside it. */
  reason: string
}

export function collectUomNotes(items: NormalisedItem[]): UomNote[] {
  const notes: UomNote[] = []

  for (const item of items) {
    for (const offer of item.offers) {
      if (!offer.converted) continue
      notes.push({
        item_code: item.item_code,
        item_name: item.name,
        enquiry_row_no: item.enquiry_row_no,
        supplier: offer.supplier,
        rfq_uom: offer.raw.rfq_uom,
        sq_uom: offer.raw.sq_uom,
        factor: offer.factor,
        quoted: offer.raw.rate,
        unitInBase: offer.unitInBase,
        handled: 'converted',
        reason: '',
      })
    }

    for (const dropped of item.excluded) {
      notes.push({
        item_code: item.item_code,
        item_name: item.name,
        enquiry_row_no: item.enquiry_row_no,
        supplier: dropped.supplier,
        rfq_uom: dropped.raw.rfq_uom,
        sq_uom: dropped.raw.sq_uom,
        factor: null,
        quoted: dropped.raw.rate,
        unitInBase: null,
        handled: 'excluded',
        reason: dropped.reason,
      })
    }
  }

  return notes
}

export interface StrategyPreview {
  total: number
  supplierCount: number
}

/**
 * What a strategy would cost, WITHOUT applying it — so the four cards can be compared before
 * the user commits to one (prototype `strategyStats`).
 */
export function previewStrategy(items: NormalisedItem[], strategy: Strategy): StrategyPreview {
  const allocations = computeAllocation(items, strategy)
  const totals = summariseAll(items, allocations)
  return { total: totals.cost, supplierCount: totals.suppliersUsed }
}

/* ============ cell interaction ============ */

/**
 * Clicking a supplier's cell. Allocating takes what is still needed, capped by that
 * supplier's stock; clicking an allocated cell clears it (prototype `toggleCell`).
 *
 * `current` must already be scoped to this item (`rowsForItem`); returns the replacement rows
 * for just this item — merge back with `withItemRows`.
 */
/**
 * Choosing the ONE supplier for a line — the whole allocation, not a share of it.
 *
 * ---- Why this is not `toggleAllocation` with a cap ----
 *
 * `toggleAllocation` builds a SPLIT: a second tick appends a row and takes only the quantity the
 * first left over. That is right where a line genuinely spans suppliers to cover stock, and wrong
 * everywhere else — most companies buy an item from one supplier, so a second tick is a
 * CORRECTION, not an addition. Under the splitting rule it read as the app ignoring the click:
 * the first supplier kept most of the quantity and the new one picked up the remainder.
 *
 * So this REPLACES. Re-picking the supplier already chosen clears it, which makes the control a
 * radio that can also be switched off — the one thing a plain radio cannot express, and the
 * purchaser needs it to undo a mis-click.
 *
 * The quantity is the LINE's, capped by what the supplier actually has: allocating more than the
 * offer's stock would promise the customer goods nobody has quoted for. `current` is ignored for
 * sizing, deliberately — the previous supplier's share is not a constraint on the new one.
 */
export function pickSoleSupplier(
  item: NormalisedItem,
  supplier: string,
  current: AllocationRow[],
): AllocationRow[] {
  const offer = item.bySupplier.get(supplier)
  // A supplier with no live offer cannot be allocated — the same guard `toggleAllocation` uses.
  if (!offer) return current.filter((row) => item.bySupplier.has(row.supplier))

  // Already the chosen one → clear it.
  if (current.length === 1 && current[0].supplier === supplier) return []

  return [makeRow(item, offer, Math.min(offer.stock || item.qty, item.qty))]
}

export function toggleAllocation(item: NormalisedItem, supplier: string, current: AllocationRow[]): AllocationRow[] {
  const offer = item.bySupplier.get(supplier)
  if (!offer) return current

  if (current.some((row) => row.supplier === supplier)) {
    return current.filter((row) => row.supplier !== supplier)
  }

  // Count only live offers, so the quantity handed out matches the "N left" badge, which is
  // computed the same way in summariseItem.
  const allocated = current.reduce(
    (sum, row) => (item.bySupplier.has(row.supplier) ? sum + row.allocated_qty : sum),
    0,
  )
  const remaining = item.qty - allocated
  const qty = remaining > 0 ? Math.min(offer.stock || remaining, remaining) : Math.min(offer.stock || item.qty, item.qty)
  return [...current, makeRow(item, offer, qty)]
}

/**
 * Typing a quantity into an allocated cell; zero removes the supplier. `current` must already
 * be scoped to this item — see `toggleAllocation`.
 */
export function setAllocationQty(
  item: NormalisedItem,
  current: AllocationRow[],
  supplier: string,
  qty: number,
): AllocationRow[] {
  const without = current.filter((row) => row.supplier !== supplier)
  const clean = Math.max(0, qty)
  if (clean === 0) return without

  const offer = item.bySupplier.get(supplier)
  if (!offer) return without // no live offer to price this row from — drop rather than fabricate a rate
  return [...without, makeRow(item, offer, clean)]
}

/**
 * `comparison.build_quotation` is idempotent: if a quotation already exists for the enquiry it
 * is REUSED as-is, never rebuilt from the allocation just saved
 * (`useComparison.ts`'s `BuildQuotationResult.created` comment). `created: false` means the
 * document ComparePage is about to send the purchaser to still prices an EARLIER split — the
 * customer stays billed on that one while the purchaser believes today's split took effect.
 *
 * Pinned as its own function, the way `folderBadge` (`lib/mail.ts`) pins a display decision,
 * because the two branches differ in something that must never be gotten backwards: 'navigate'
 * leaves ComparePage straight away, 'warn-reused' must not.
 */
export type BuildQuotationOutcome = 'navigate' | 'warn-reused'

export function buildQuotationOutcome(created: boolean): BuildQuotationOutcome {
  return created ? 'navigate' : 'warn-reused'
}
